import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  Camera,
  Megaphone,
  Monitor,
  Compass,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSiteContent } from "../context/SiteContentContext";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

const iconMap = {
  camera: Camera,
  megaphone: Megaphone,
  monitor: Monitor,
  compass: Compass,
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const { getMergedServicePage } = useSiteContent();
  const data = getMergedServicePage(lang, slug);

  if (!data) return <Navigate to="/services" replace />;

  const Icon = iconMap[data.icon] || Sparkles;
  // alternate hero style based on slug
  const heroIsDark = slug === "ads" || slug === "consultanta";

  return (
    <main className="bg-white" data-testid={`service-page-${slug}`}>
      {/* Hero */}
      <section
        className={`relative overflow-hidden pt-40 pb-24 sm:pt-52 sm:pb-32 ${
          heroIsDark ? "bg-[#3e2723]" : "bg-[#fbd9e5]"
        }`}
      >
        <div
          aria-hidden
          className={`pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full blur-3xl ${
            heroIsDark ? "bg-[#fbd9e5]/15" : "bg-white/40"
          }`}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 rounded-full border px-4 py-2"
            style={{
              borderColor: heroIsDark ? "rgba(251,217,229,0.25)" : "rgba(62,39,35,0.15)",
              background: heroIsDark ? "rgba(251,217,229,0.08)" : "rgba(255,255,255,0.5)",
            }}
          >
            <Icon className={`h-3.5 w-3.5 ${heroIsDark ? "text-[#fbd9e5]" : "text-[#3e2723]"}`} />
            <span
              className={`text-[11px] uppercase tracking-[0.25em] ${
                heroIsDark ? "text-[#fbd9e5]" : "text-[#3e2723]"
              }`}
            >
              {data.eyebrow}
            </span>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className={`mt-6 font-display text-5xl sm:text-7xl lg:text-[5.5rem] font-black tracking-tighter leading-[0.95] max-w-5xl ${
              heroIsDark ? "text-[#fbd9e5]" : "text-[#3e2723]"
            }`}
          >
            {data.title}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className={`mt-8 max-w-2xl text-lg leading-relaxed ${
              heroIsDark ? "text-[#fbd9e5]/80" : "text-[#3e2723]/75"
            }`}
          >
            {data.lead}
          </motion.p>
        </div>
      </section>

      {/* Intro / Prezentare */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid gap-12 lg:grid-cols-12 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter text-[#3e2723] leading-[1.05]">
              {data.intro_title}
            </h2>
            <p className="mt-6 text-lg text-[#7a5c55] leading-relaxed">{data.intro_body}</p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#3e2723] text-[#fbd9e5] px-6 py-3.5 text-[14px] font-semibold hover:bg-[#2a1a17] hover:-translate-y-0.5 transition-all duration-300"
              data-testid={`service-${slug}-intro-cta`}
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-6 order-1 lg:order-2"
          >
            <div className="relative overflow-hidden rounded-[2.5rem] aspect-[4/3]">
              <img
                src={data.intro_image}
                alt={data.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#3e2723]/10 via-transparent to-[#fbd9e5]/20" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* De ce noi */}
      <section className="py-24 sm:py-32 bg-[#faf8f8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#3e2723] max-w-2xl"
          >
            {data.why_title}
          </motion.h2>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {data.why_points.map((p, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="group rounded-[1.75rem] bg-white border border-[#3e2723]/8 p-7 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(62,39,35,0.08)] transition-all duration-500"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] font-display font-bold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-[#3e2723] leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-[#7a5c55]">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#3e2723] max-w-2xl"
          >
            {data.features_title}
          </motion.h2>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.features.map((f, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="relative rounded-[1.75rem] border border-[#3e2723]/10 bg-[#fbd9e5]/40 p-6 hover:bg-[#fbd9e5] hover:-translate-y-1 transition-all duration-500"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-[#3e2723] leading-snug">
                  {f.title}
                </h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-[#3e2723]/75">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Landscape CTA Card */}
      <section className="py-16 sm:py-24 bg-[#faf8f8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="relative overflow-hidden rounded-[2.5rem] sm:rounded-[3rem] bg-gradient-to-br from-[#3e2723] via-[#3e2723] to-[#2a1a17] p-8 sm:p-14 lg:p-16"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-32 -right-24 h-[460px] w-[460px] rounded-full bg-[#fbd9e5]/20 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-[#fbd9e5]/10 blur-3xl"
            />
            <div className="relative grid gap-8 lg:grid-cols-12 items-center">
              <div className="lg:col-span-8">
                <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#fbd9e5]/70">
                  <Sparkles className="h-3.5 w-3.5" /> {data.eyebrow}
                </span>
                <h3 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.98] text-[#fbd9e5]">
                  {data.cta_title}
                </h3>
                <p className="mt-5 text-base sm:text-lg text-[#fbd9e5]/75 max-w-xl">
                  {data.cta_subtitle}
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-[#fbd9e5] text-[#3e2723] px-7 py-4 text-[14px] font-semibold hover:bg-white hover:-translate-y-1 shadow-lg transition-all duration-300 whitespace-nowrap"
                  data-testid={`service-${slug}-cta`}
                >
                  {t.nav.cta}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
